import { useState, useEffect } from 'react'
import api from '../services/api'
import websocketService from '../services/websocket'

// Central player-meta feed for navigation surfaces (sidebar, mobile nav).
// Fetches level/badge state once and keeps it fresh via WebSocket events,
// so multiple nav components never duplicate polling or subscriptions.
export const usePlayerMeta = () => {
    const [globalLevel, setGlobalLevel] = useState(1)
    const [globalTotalExperience, setGlobalTotalExperience] = useState(0)
    const [hasDailyReward, setHasDailyReward] = useState(false)
    const [claimableAchievements, setClaimableAchievements] = useState(0)
    const [hasCollectibles, setHasCollectibles] = useState(false)

    const fetchPlayerLevel = async () => {
        try {
            const response = await api.get('/player/state')
            if (response.data) {
                setGlobalLevel(response.data.global_level || 1)
                setGlobalTotalExperience(response.data.global_total_experience || 0)
                setHasDailyReward(response.data.daily_reward_available || false)
            }
        } catch (error) {
            console.error('Failed to fetch player level:', error)
        }
    }

    const checkClaimableAchievements = async () => {
        try {
            const response = await api.get('/achievements/', { params: { status: 'unlocked' } })
            const achievements = Array.isArray(response.data) ? response.data : []
            setClaimableAchievements(
                achievements.filter(ach => ach.status === 'unlocked' && !ach.rewards_claimed).length
            )
        } catch (error) {
            console.error('Failed to check claimable achievements:', error)
        }
    }

    const checkMarketCollectibles = async () => {
        try {
            const response = await api.get('/market/my-listings')
            const listings = Array.isArray(response.data?.listings) ? response.data.listings : []
            setHasCollectibles(listings.some(listing => listing.status === 'sold' || listing.status === 'expired'))
        } catch (error) {
            console.error('Failed to check market collectibles:', error)
        }
    }

    useEffect(() => {
        fetchPlayerLevel()
        checkClaimableAchievements()
        checkMarketCollectibles()
    }, [])

    useEffect(() => {
        const handleAchievementEvent = () => setTimeout(checkClaimableAchievements, 300)
        const handleMarketEvent = () => setTimeout(checkMarketCollectibles, 300)
        const handlePlayerLevelEvent = () => setTimeout(fetchPlayerLevel, 300)

        websocketService.on('achievement.unlocked', handleAchievementEvent)
        websocketService.on('achievement.progress', handleAchievementEvent)
        websocketService.on('achievement.reward_claimed', handleAchievementEvent)
        websocketService.on('market.item.sold', handleMarketEvent)
        websocketService.on('market.item.expired', handleMarketEvent)
        websocketService.on('market.item.bought', handleMarketEvent)
        websocketService.on('player.skill.experience_gained', handlePlayerLevelEvent)
        websocketService.on('player.global_level_up', handlePlayerLevelEvent)
        websocketService.on('skill.level_up', handlePlayerLevelEvent)
        websocketService.on('scavenging_delayed.run.completed', handlePlayerLevelEvent)
        websocketService.on('gathering.completed', handlePlayerLevelEvent)
        websocketService.on('crafting.job.completed', handlePlayerLevelEvent)
        websocketService.on('production.completed', handlePlayerLevelEvent)

        window.addEventListener('badge-refresh-achievements', handleAchievementEvent)
        window.addEventListener('badge-refresh-market', handleMarketEvent)
        window.addEventListener('badge-refresh-daily-reward', fetchPlayerLevel)
        window.addEventListener('badge-refresh-player-level', fetchPlayerLevel)

        return () => {
            websocketService.off('achievement.unlocked', handleAchievementEvent)
            websocketService.off('achievement.progress', handleAchievementEvent)
            websocketService.off('achievement.reward_claimed', handleAchievementEvent)
            websocketService.off('market.item.sold', handleMarketEvent)
            websocketService.off('market.item.expired', handleMarketEvent)
            websocketService.off('market.item.bought', handleMarketEvent)
            websocketService.off('player.skill.experience_gained', handlePlayerLevelEvent)
            websocketService.off('player.global_level_up', handlePlayerLevelEvent)
            websocketService.off('skill.level_up', handlePlayerLevelEvent)
            websocketService.off('scavenging_delayed.run.completed', handlePlayerLevelEvent)
            websocketService.off('gathering.completed', handlePlayerLevelEvent)
            websocketService.off('crafting.job.completed', handlePlayerLevelEvent)
            websocketService.off('production.completed', handlePlayerLevelEvent)

            window.removeEventListener('badge-refresh-achievements', handleAchievementEvent)
            window.removeEventListener('badge-refresh-market', handleMarketEvent)
            window.removeEventListener('badge-refresh-daily-reward', fetchPlayerLevel)
            window.removeEventListener('badge-refresh-player-level', fetchPlayerLevel)
        }
    }, [])

    return {
        globalLevel,
        globalTotalExperience,
        hasDailyReward,
        claimableAchievements,
        hasCollectibles,
        refreshLevel: fetchPlayerLevel,
        // badgeKey -> truthy value for resolveBadge(); numbers render as counts
        badges: {
            achievements: claimableAchievements,
            market: hasCollectibles,
            daily: hasDailyReward,
        },
    }
}

export default usePlayerMeta
